import { Button, Tab, Tabs } from '@mui/material'
import { FC, useState } from 'react'
import { EditButton, Show, useGetList, useRecordContext, useRedirect, useTranslate } from 'react-admin'
import { CERTIFICATE_ORGANIZATION_SWITCH_SCOPE } from '../../config'
import { useHasScope } from '../../hooks/useHasScope'
import { useSettingsCrumbs } from '../../hooks/useSettingsCrumbs'
import useSwitchOrganization from '../../hooks/useSwitchOrganization'
import { Card, Muted, PageHeader, Property, pageSx } from '../../components/ui'
import type { AuditLogOut, CertificateOut, MunicipalityOut } from '../../types/apiRequests'
import type { OrganizationRecord } from '../../types/apiTypes'
import { AuditTimeline, CertificatesTable, RELATED_LIMIT, useLimitHint } from '../Certificates/CertificateRelated'
import { linkedApps } from './Organizations.list'

// "Wissel naar deze organisatie": the other organization's certificates are only reachable as the active organization.
const SwitchButton: FC<{ record: OrganizationRecord }> = ({ record }) => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const switchOrganization = useSwitchOrganization()
  return (
    <Button
      variant="outlined"
      onClick={() => {
        switchOrganization(record.organizationId, record.name)
        redirect('/certificate')
      }}
    >
      {translate('resources.organization.switchTo')}
    </Button>
  )
}

const DetailsTab: FC<{ record: OrganizationRecord }> = ({ record }) => {
  const translate = useTranslate()
  const hasApps = Object.values(record.apps ?? {}).some(Boolean)
  return (
    <Card title={translate('settings.tabs.details')}>
      <Property label={translate('resources.organization.fields.name')}>{record.name}</Property>
      <Property label={translate('resources.organization.fields.organizationId')}>
        <Muted mono plain>{record.organizationId}</Muted>
      </Property>
      <Property label={translate('resources.organization.fields.apps')}>
        {hasApps ? linkedApps(record) : <Muted>—</Muted>}
      </Property>
      <Property label={translate('settings.organization.hasCertificates')}>
        {translate(record.hasCertificates ? 'settings.organization.yes' : 'settings.organization.no')}
      </Property>
    </Card>
  )
}

// Reads the organization's data through the organization header (switch scope); rows are not
// clickable since another organization's certificate would 404 under the current one.
const CertificatesTab: FC<{ record: OrganizationRecord }> = ({ record }) => {
  const translate = useTranslate()
  const { data, total, isLoading } = useGetList<CertificateOut>('certificate', {
    sort: { field: 'created', order: 'DESC' },
    pagination: { page: 1, perPage: RELATED_LIMIT },
    meta: { organizationId: record.organizationId },
  })
  const hint = useLimitHint(total)
  return (
    <Card
      title={translate('settings.tabs.certificates')}
      subtitle={hint}
      actions={<SwitchButton record={record} />}
      flush
    >
      <CertificatesTable
        certificates={data}
        isLoading={isLoading}
        clickable={false}
        emptyTitle={translate('resources.certificates.empty.title')}
        emptyText={translate('settings.organization.noCertificatesText')}
      />
    </Card>
  )
}

const LogsTab: FC<{ record: OrganizationRecord }> = ({ record }) => {
  const translate = useTranslate()
  const meta = { organizationId: record.organizationId }
  const { data, total, isLoading, error } = useGetList<AuditLogOut>('auditlog', {
    sort: { field: 'created', order: 'DESC' },
    pagination: { page: 1, perPage: RELATED_LIMIT },
    meta,
  })
  const hint = useLimitHint(total)
  // Certificates and municipalities to show "hostname · gemeente" per log line.
  const pagination = { page: 1, perPage: RELATED_LIMIT }
  const certificates = useGetList<CertificateOut>('certificate', { pagination, meta })
  const municipalities = useGetList<MunicipalityOut>('municipality', { pagination, meta })
  return (
    <Card title={translate('settings.tabs.logs')} subtitle={hint} actions={<SwitchButton record={record} />}>
      <AuditTimeline
        logs={data}
        isLoading={isLoading}
        error={error}
        certificates={certificates.data}
        municipalities={municipalities.data}
        linkCertificates={false}
      />
    </Card>
  )
}

const OrganizationShowContent: FC = () => {
  const record = useRecordContext<OrganizationRecord>()
  const translate = useTranslate()
  const [tab, setTab] = useState(0)
  // Cross-organization data needs the switch scope; without it only the details tab exists.
  const canSwitch = useHasScope(CERTIFICATE_ORGANIZATION_SWITCH_SCOPE)
  const crumbs = useSettingsCrumbs('organization', record?.name)
  if (!record) return null

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={record.name}
        subtitle={<Muted mono plain>{record.organizationId}</Muted>}
        actions={<EditButton variant="contained" />}
      />
      <Tabs value={tab} onChange={(_, value: number) => setTab(value)}>
        <Tab label={translate('settings.tabs.details')} />
        {canSwitch && <Tab label={translate('settings.tabs.certificates')} />}
        {canSwitch && <Tab label={translate('settings.tabs.logs')} />}
      </Tabs>
      {tab === 0 && <DetailsTab record={record} />}
      {canSwitch && tab === 1 && <CertificatesTab record={record} />}
      {canSwitch && tab === 2 && <LogsTab record={record} />}
    </>
  )
}

const OrganizationsShow: FC = () => (
  <Show component="div" actions={false} sx={pageSx}>
    <OrganizationShowContent />
  </Show>
)

export default OrganizationsShow
